import {
  Fragment, useCallback, useEffect, useRef, useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  IconButton, Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
// import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import ReportFilter, { updateReportParams } from './components/ReportFilter';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import ReportsMenu from './components/ReportsMenu';
import PositionValue from '../common/components/PositionValue';
import ColumnSelect from './components/ColumnSelect';
import usePositionAttributes from '../common/attributes/usePositionAttributes';
import { useCatch } from '../reactHelper';
import MapView from '../map/core/MapView';
import MapRoutePath from '../map/MapRoutePath';
import MapRoutePoints from '../map/MapRoutePoints';
import MapPositions from '../map/MapPositions';
import useReportStyles from './common/useReportStyles';
import TableShimmer from '../common/components/TableShimmer';
import MapCamera from '../map/MapCamera';
import MapGeofence from '../map/MapGeofence';
import scheduleReport from './common/scheduleReport';
import MapScale from '../map/MapScale';
import { useRestriction } from '../common/util/permissions';
import CollectionActions from '../settings/components/CollectionActions';
import fetchOrThrow from '../common/util/fetchOrThrow';
import SelectField from '../common/components/SelectField';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useAttributePreference, usePreference } from '../common/util/preferences';
import { useTheme } from '@mui/material/styles';
import exportExcel from '../common/util/exportExcel';
import {
  formatTime,
  formatDistance,
  formatSpeed,
  formatNumericHours,
  formatVolume,
  formatBoolean,
  formatNumber,
  formatCoordinate,
  formatVoltage,
  formatPercentage,
  formatTemperature,
  formatConsumption,
  formatAlarm,
  formatAltitude,
} from '../common/util/formatter';
import { sessionActions } from '../store';
import { PrintLayout } from './common/print';
// 2ECOMP: Mini-map components
import { MiniMapCell, MapExpandModal } from '../common/components/premium';

const PositionsReportPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { classes } = useReportStyles();
  const t = useTranslation();
  const theme = useTheme();

  const [searchParams, setSearchParams] = useSearchParams();

  const positionAttributes = usePositionAttributes(t);

  const readonly = useRestriction('readonly');
  const devices = useSelector((state) => state.devices.items, shallowEqual);
  const printing = useSelector((state) => state.session.printing ?? false);

  const distanceUnit = useAttributePreference('distanceUnit');
  const speedUnit = useAttributePreference('speedUnit');
  const altitudeUnit = useAttributePreference('altitudeUnit');
  const volumeUnit = useAttributePreference('volumeUnit');
  const coordinateFormat = usePreference('coordinateFormat');

  const [available, setAvailable] = useState([]);
  const [columns, setColumns] = useState(['fixTime', 'latitude', 'longitude', 'speed', 'address']);
  const [items, setItems] = useState([]);
  const geofenceId = searchParams.has('geofenceId') ? parseInt(searchParams.get('geofenceId')) : null;
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [hasResults, setHasResults] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  // 2ECOMP: Expanded map state
  const [expandedMap, setExpandedMap] = useState(null);

  const selectedIcon = useRef();

  useEffect(() => {
    if (selectedIcon.current) {
      selectedIcon.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [selectedIcon.current]);

  useEffect(() => {
    const handleAfterPrint = () => {
      dispatch(sessionActions.setPrinting(false));
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [dispatch]);

  // nativePostMessage removido - agora usamos toolbar manual para print

  const onMapPointClick = useCallback((positionId) => {
    setSelectedItem(items.find((it) => it.id === positionId));
  }, [items, setSelectedItem]);

  const loadPositions = async ({ deviceIds = [], from, to }) => {
    const query = new URLSearchParams({ from, to });
    if (geofenceId) {
      query.append('geofenceId', geofenceId);
    }
    deviceIds.forEach((deviceId) => query.append('deviceId', deviceId));
    setLoading(true);
    setSelectedItem(null);
    try {
      const response = await fetchOrThrow(`/api/positions?${query.toString()}`, {
        headers: { Accept: 'application/json' },
      });
      const data = await response.json();
      const keySet = new Set();
      const keyList = [];
      data.forEach((position) => {
        Object.keys(position).forEach((it) => keySet.add(it));
        Object.keys(position.attributes).forEach((it) => keySet.add(it));
      });
      ['id', 'deviceId', 'outdated', 'network', 'attributes'].forEach((key) => keySet.delete(key));
      Object.keys(positionAttributes).forEach((key) => {
        if (keySet.has(key)) {
          keyList.push(key);
          keySet.delete(key);
        }
      });
      setAvailable([...keyList, ...keySet].map((key) => [key, positionAttributes[key]?.name || key]));
      setItems(data);
      setHasResults(data.length > 0);
    } finally {
      setLoading(false);
    }
  };

  const onShow = useCatch(async ({ deviceIds, from, to }) => {
    setSelectedDeviceIds(deviceIds);
    setFromDate(from || '');
    setToDate(to || '');
    await loadPositions({ deviceIds, from, to });
  });

  const getPositionExportValue = (position, key) => {
    const attribute = positionAttributes[key] || {};
    const hasProperty = Object.prototype.hasOwnProperty.call(position, key);
    const value = hasProperty ? position[key] : position.attributes?.[key];

    if (value === undefined || value === null) {
      return '';
    }

    if (key === 'deviceId') {
      return devices[value]?.name || `${value}`;
    }

    if (key === 'address') {
      return position.address || value;
    }

    if (Array.isArray(value)) {
      return value.join(', ');
    }

    if (/Time$/i.test(key)) {
      return formatTime(value, 'seconds');
    }

    switch (key) {
      case 'latitude':
        return formatCoordinate('latitude', Number(value), coordinateFormat);
      case 'longitude':
        return formatCoordinate('longitude', Number(value), coordinateFormat);
      case 'speed':
      case 'obdSpeed':
        return formatSpeed(Number(value), speedUnit, t);
      case 'course':
        return formatNumber(Number(value));
      case 'altitude':
        return formatAltitude(Number(value), altitudeUnit, t);
      case 'power':
      case 'battery':
        return formatVoltage(Number(value), t);
      case 'batteryLevel':
        return formatPercentage(Number(value));
      case 'volume':
        return formatVolume(Number(value), volumeUnit, t);
      case 'fuelConsumption':
        return formatConsumption(Number(value), t);
      case 'coolantTemp':
      case 'engineTemp':
        return formatTemperature(Number(value));
      case 'alarm':
        return formatAlarm(value, t);
      default:
        break;
    }

    switch (attribute.dataType) {
      case 'speed':
        return formatSpeed(Number(value), speedUnit, t);
      case 'distance':
        return formatDistance(Number(value), distanceUnit, t);
      case 'volume':
        return formatVolume(Number(value), volumeUnit, t);
      case 'hours':
        return formatNumericHours(Number(value), t);
      default:
        break;
    }

    if (attribute.type === 'boolean') {
      return formatBoolean(Boolean(value), t);
    }

    if (typeof value === 'number') {
      return formatNumber(Number(value));
    }

    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return '';
      }
    }

    return `${value}`;
  };

  const onExport = useCatch(async () => {
    if (!items.length) {
      return;
    }

    const sheets = new Map();
    items.forEach((item) => {
      const deviceName = devices[item.deviceId]?.name || t('sharedUnknown');
      if (!sheets.has(deviceName)) {
        sheets.set(deviceName, []);
      }
      const row = {};
      columns.forEach((key) => {
        const header = positionAttributes[key]?.name || key;
        row[header] = getPositionExportValue(item, key);
      });
      sheets.get(deviceName).push(row);
    });

    await exportExcel(t('reportPositions'), `${t('reportPositions')}.xlsx`, sheets, theme);
  });

  const onSchedule = useCatch(async (deviceIds, groupIds, report) => {
    report.type = 'route';
    await scheduleReport(deviceIds, groupIds, report);
    navigate('/reports/scheduled');
  });

  const onPrint = useCatch(async ({ deviceIds = [], from, to } = {}) => {
    const effectiveDeviceIds = deviceIds.length ? deviceIds : selectedDeviceIds;
    const effectiveFrom = from ?? fromDate;
    const effectiveTo = to ?? toDate;

    if (!effectiveDeviceIds.length || !effectiveFrom || !effectiveTo) {
      return;
    }

    if (!items.length) {
      await loadPositions({ deviceIds: effectiveDeviceIds, from: effectiveFrom, to: effectiveTo });
    }

    setSelectedDeviceIds(effectiveDeviceIds);
    setFromDate(effectiveFrom);
    setToDate(effectiveTo);
    dispatch(sessionActions.setPrinting(true));
  });

  const deviceNames = selectedDeviceIds
    .map((deviceId) => devices?.[deviceId]?.name || devices?.[deviceId]?.uniqueId || `${deviceId}`)
    .filter(Boolean)
    .join(', ');

  if (printing) {
    return (
      <PrintLayout
        title={t('reportPositions')}
        from={fromDate}
        to={toDate}
        deviceName={deviceNames}
        onReady={() => window.print()}
      >
        <Table className="print-table">
          <TableHead>
            <TableRow>
              <TableCell>{t('sharedDevice')}</TableCell>
              {/* 2ECOMP: Mini-map column for print */}
              <TableCell sx={{ width: 70, textAlign: 'center' }}>{t('positionMap') || 'Mapa'}</TableCell>
              {columns.map((key) => (
                <TableCell key={key}>{positionAttributes[key]?.name || key}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.slice(0, 4000).map((item) => (
              <TableRow key={`print-${item.id}`}>
                <TableCell>{devices?.[item.deviceId]?.name || item.deviceId}</TableCell>
                {/* 2ECOMP: Mini-map cell for print */}
                <TableCell sx={{ textAlign: 'center', py: 0.5 }}>
                  <MiniMapCell
                    latitude={item.latitude}
                    longitude={item.longitude}
                    speed={item.speed}
                    size="xs"
                    showExpandButton={false}
                    forPrint
                  />
                </TableCell>
                {columns.map((key) => (
                  <TableCell key={key}>
                    <PositionValue
                      position={item}
                      property={Object.prototype.hasOwnProperty.call(item, key) ? key : null}
                      attribute={Object.prototype.hasOwnProperty.call(item, key) ? null : key}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </PrintLayout>
    );
  }

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={['reportTitle', 'reportPositions']}>
      <div className={classes.container}>
        {selectedItem && (
          <div className={classes.containerMap}>
            <MapView>
              <MapGeofence />
              {[...new Set(items.map((it) => it.deviceId))].map((deviceId) => {
                const positions = items.filter((position) => position.deviceId === deviceId);
                return (
                  <Fragment key={deviceId}>
                    <MapRoutePath positions={positions} />
                    <MapRoutePoints positions={positions} onClick={onMapPointClick} />
                  </Fragment>
                );
              })}
              <MapPositions positions={[selectedItem]} titleField="fixTime" />
            </MapView>
            <MapScale />
            <MapCamera positions={items} />
          </div>
        )}
        <div className={classes.containerMain}>
          <div className={classes.header}>
            <ReportFilter
              onShow={onShow}
              onExport={onExport}
              onPrint={onPrint}
              onSchedule={onSchedule}
              deviceType="single"
              loading={loading}
              hasResults={hasResults}
            >
              <div className={classes.filterItem}>
                <SelectField
                  value={geofenceId}
                  onChange={(e) => {
                    const values = e.target.value ? [e.target.value] : [];
                    updateReportParams(searchParams, setSearchParams, 'geofenceId', values);
                  }}
                  endpoint="/api/geofences"
                  label={t('sharedGeofence')}
                  fullWidth
                />
              </div>
              <ColumnSelect
                columns={columns}
                setColumns={setColumns}
                columnsArray={available}
                rawValues
                disabled={!items.length}
              />
            </ReportFilter>
          </div>
          <Table id="positions-report-table">
            <TableHead>
              <TableRow>
                <TableCell className={classes.columnAction} />
                {/* 2ECOMP: Mini-map column */}
                <TableCell sx={{ width: 100, textAlign: 'center' }}>{t('positionMap') || 'Mapa'}</TableCell>
                {columns.map((key) => (<TableCell key={key}>{positionAttributes[key]?.name || key}</TableCell>))}
                <TableCell className={classes.columnAction} />
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading ? items.slice(0, 4000).map((item) => (
                <TableRow key={item.id}>
                  <TableCell className={classes.columnAction} padding="none">
                    {selectedItem === item ? (
                      <IconButton size="small" onClick={() => setSelectedItem(null)} ref={selectedIcon}>
                        <GpsFixedIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <IconButton size="small" onClick={() => setSelectedItem(item)}>
                        <LocationSearchingIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                  {/* 2ECOMP: Mini-map cell */}
                  <TableCell sx={{ textAlign: 'center', py: 0.5 }}>
                    <MiniMapCell
                      latitude={item.latitude}
                      longitude={item.longitude}
                      speed={item.speed}
                      size="xs"
                      onClick={() => setExpandedMap({
                        latitude: item.latitude,
                        longitude: item.longitude,
                        title: t('reportPositions'),
                        subtitle: formatTime(item.fixTime, 'seconds'),
                        deviceName: devices[item.deviceId]?.name,
                      })}
                      tooltipTitle={t('mapExpand') || 'Expandir mapa'}
                    />
                  </TableCell>
                  {columns.map((key) => (
                    <TableCell key={key}>
                      <PositionValue
                        position={item}
                        property={item.hasOwnProperty(key) ? key : null}
                        attribute={item.hasOwnProperty(key) ? null : key}
                      />
                    </TableCell>
                  ))}
                  <TableCell className={classes.actionCellPadding}>
                    <CollectionActions
                      itemId={item.id}
                      endpoint="positions"
                      readonly={readonly}
                      setTimestamp={() => {
                        setItems(items.filter((position) => position.id !== item.id));
                      }}
                    />
                  </TableCell>
                </TableRow>
              )) : (<TableShimmer columns={columns.length + 2} startAction />)}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* 2ECOMP: Expanded map modal */}
      <MapExpandModal
        open={!!expandedMap}
        onClose={() => setExpandedMap(null)}
        latitude={expandedMap?.latitude}
        longitude={expandedMap?.longitude}
        title={expandedMap?.title}
        subtitle={expandedMap?.subtitle}
        deviceName={expandedMap?.deviceName}
      />
    </PageLayout>
  );
};

export default PositionsReportPage;
